import { Link } from "react-router-dom";
import { ScrollText } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { Footer } from "@/components/Footer";
import { CONTACT } from "@/lib/siteData";
import { BRAND } from "@/lib/brand";

const UPDATED = "22 June 2026";

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="text-lg md:text-lg font-bold text-slate-900">{title}</h2>
    <div className="mt-3 space-y-3 text-sm md:text-base text-slate-600 leading-relaxed">{children}</div>
  </section>
);

export default function TermsOfService() {
  return (
    <div data-testid="terms-page" className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <BrandLogo to="/" size="sm" />
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
          <div className="flex items-center gap-2 text-cyan-600">
            <ScrollText className="w-5 h-5" />
            <span className="text-[11px] uppercase tracking-[0.2em] font-bold">The Fine Print</span>
          </div>
          <h1 className="font-display mt-3 text-4xl sm:text-5xl font-bold text-slate-900">Terms of Service</h1>
          <p className="mt-3 text-slate-600">Last updated: {UPDATED}</p>

          <p className="mt-8 text-sm md:text-base text-slate-600 leading-relaxed">
            These terms govern your use of the website, portals and water testing services provided by{" "}
            {BRAND.name}{BRAND.formerName ? `, formerly ${BRAND.formerName},` : ""} ("AWARE", "we", "us"). By booking a
            test, creating an account or using this site, you agree to these terms. Nothing in these terms
            excludes rights you have under the Australian Consumer Law.
          </p>

          <Section title="1. Our services">
            <p>
              AWARE is an independent Australian water testing laboratory. We collect and/or receive water
              samples, analyse them, and provide plain-English results, reports and practical
              recommendations. Service inclusions, pricing and turnaround times are as described on this
              site or in your booking confirmation.
            </p>
          </Section>

          <Section title="2. Bookings, access and samples">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>You must provide accurate property, contact and access details when booking.</li>
              <li>Where a technician visit is booked, you agree to provide safe and reasonable access at the scheduled time. Missed appointments without reasonable notice may incur a rebooking fee.</li>
              <li>For samples you collect yourself, results depend on you following the supplied sampling instructions. Incorrectly collected, stored or delayed samples can affect accuracy.</li>
              <li>All test results relate solely to the sample as received by the laboratory, at the time of testing.</li>
            </ul>
          </Section>

          <Section title="3. Results, reports and limitations">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Results and reports are provided for informational and water quality assessment purposes.</li>
              <li>Water quality can change over time — a result reflects the sample tested, not a permanent guarantee of your water supply.</li>
              <li>Where regulatory, legal, council, food production, compliance or certification requirements apply, you should confirm whether accredited laboratory testing is required for your specific application before relying on our reports.</li>
              <li>Recommendations are general guidance. Treatment, plumbing or remediation work should be carried out by appropriately qualified professionals.</li>
            </ul>
          </Section>

          <Section title="4. Pricing and payment">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Prices are in Australian dollars and include GST unless stated otherwise.</li>
              <li>Payment is processed securely by our payment provider (Stripe). We do not store full card numbers.</li>
              <li>Memberships and subscriptions renew as described at purchase and can be cancelled from your account or by contacting us before the next renewal.</li>
              <li>Refunds are handled in line with the Australian Consumer Law. If testing has not yet commenced, contact us and we will do our best to help.</li>
            </ul>
          </Section>

          <Section title="5. Your account">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>You are responsible for keeping your sign-in details secure and for activity under your account.</li>
              <li>You may sign in with an email and password, or with Google or Facebook. Social sign-in is an authentication method only — it does not change what your account can access.</li>
              <li>You must not attempt to access another customer's results, another partner's records, or any area you are not authorised to use.</li>
              <li>We may suspend accounts that breach these terms or compromise the security of the service.</li>
            </ul>
          </Section>

          <Section title="6. Partners and trade accounts">
            <p>
              Partner, trade and property-manager accounts are subject to approval and to any separate
              agreement in place with AWARE. Partner access is limited to the bookings and records that
              partner referred or manages.
            </p>
          </Section>

          <Section title="7. Acceptable use">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Do not misuse the site — including attempting to breach security, scrape data, interfere with other users, or submit false or misleading information.</li>
              <li>Content on this site (including reports, text, branding and imagery) belongs to AWARE or its licensors and may not be reproduced commercially without permission. You may share your own water test reports freely.</li>
            </ul>
          </Section>

          <Section title="8. Liability">
            <p>
              To the extent permitted by law, and except for the consumer guarantees that cannot be
              excluded, our liability for any claim arising from the services is limited to resupplying the
              relevant service or refunding the amount paid for it. We are not liable for indirect or
              consequential loss, or for decisions made by third parties based on your reports.
            </p>
          </Section>

          <Section title="9. Privacy">
            <p>
              Our collection and handling of personal information is described in our{" "}
              <Link to="/privacy" className="text-cyan-700 font-semibold hover:underline">Privacy Policy</Link>.
            </p>
          </Section>

          <Section title="10. Changes and governing law">
            <p>
              We may update these terms from time to time; the current version will always be available at
              this page. These terms are governed by the laws of Queensland, Australia.
            </p>
          </Section>

          <Section title="11. Contact us">
            <p>
              {BRAND.name}
              <br />
              {CONTACT.address}
              <br />
              Phone: {CONTACT.phone}
              <br />
              Email: <a href={`mailto:${CONTACT.email}`} className="text-cyan-700 font-semibold hover:underline">{CONTACT.email}</a>
            </p>
          </Section>

          <p className="mt-12 text-sm">
            <Link to="/" className="font-semibold text-cyan-700 hover:underline">← Back to home</Link>
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}
